import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useFindMany, useGlobalAction, useEnqueue, useAction } from "@gadgetinc/react";
import { api } from "../api";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Badge,
  Input,
  Select,
  FormControl,
  FormLabel,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardBody,
  Flex,
  Spacer,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  Container,
  Heading,
  TableContainer,
} from "@chakra-ui/react";
import { 
  FiRefreshCw, 
  FiDownload, 
  FiMoreVertical, 
  FiEye, 
  FiAlertCircle,
  FiFilter,
  FiTrash2,
  FiPlay,
  FiFileText
} from "react-icons/fi";

const ClinicalDocsAdmin = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // Filter states
  const [filters, setFilters] = useState({
    institution: "",
    processingStatus: "",
    dateFrom: "",
    dateTo: "",
    search: ""
  });
  
  // Selection and batch operation states
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal states
  const { 
    isOpen: isReprocessModalOpen, 
    onOpen: onReprocessModalOpen, 
    onClose: onReprocessModalClose 
  } = useDisclosure();
  const { 
    isOpen: isExportModalOpen, 
    onOpen: onExportModalOpen, 
    onClose: onExportModalClose 
  } = useDisclosure();
  const { 
    isOpen: isOverridePassReleaseOpen, 
    onOpen: onOverridePassReleaseOpen, 
    onClose: onOverridePassReleaseClose 
  } = useDisclosure();
  const { 
    isOpen: isOverridePassNoReleaseOpen, 
    onOpen: onOverridePassNoReleaseOpen, 
    onClose: onOverridePassNoReleaseClose 
  } = useDisclosure();
  const { 
    isOpen: isReleaseFailedOpen, 
    onOpen: onReleaseFailedOpen, 
    onClose: onReleaseFailedClose 
  } = useDisclosure();

  // Selected document for override actions
  const [selectedDocForOverride, setSelectedDocForOverride] = useState(null);
  
  // Build filter object for API call
  const apiFilter = useMemo(() => {
    const filter = {
      AND: []
    };
    
    if (filters.institution) {
      // Filter by submittedBy user's institution
      filter.AND.push({
        submittedBy: {
          institution: { equals: filters.institution }
        }
      });
    }
    
    if (filters.processingStatus) {
      filter.AND.push({
        processingStatus: { equals: filters.processingStatus }
      });
    }
    
    if (filters.dateFrom) {
      filter.AND.push({
        createdAt: { greaterThanOrEqual: filters.dateFrom }
      });
    }
    
    if (filters.dateTo) {
      filter.AND.push({
        createdAt: { lessThanOrEqual: filters.dateTo }
      });
    }
    
    return filter.AND.length > 0 ? filter : {};
  }, [filters]);

  // Fetch shift processing records
  const [{ data: documents, fetching, error }, refetch] = useFindMany(
    api.shiftProcessing,
    {
      filter: apiFilter,
      search: filters.search || undefined,
      sort: { createdAt: "Descending" },
      first: 50,
      live: true, // Real-time updates
      select: {
        id: true,
        createdAt: true,
        updatedAt: true,
        processingStatus: true,
        evaluationStatus: true,
        validationStatus: true,
        auditVerdict: true,
        sourceShiftId: true,
        lastProcessedAt: true,
        structuredData: true,
        submittedBy: {
          id: true,
          email: true,
          institution: true,
          firstName: true,
          lastName: true
        },
        assignedInstructor: {
          id: true,
          email: true,
          firstName: true,
          lastName: true
        },
        userFile: {
          id: true,
          originalFilename: true,
          title: true
        },
        processor: {
          id: true,
          name: true
        }
      }
    }
  );

  // Global actions
  const [{ fetching: reprocessing }, reprocessDocument] = useGlobalAction(api.processDocument);
  const [{ fetching: batchProcessing }, batchProcess] = useGlobalAction(api.processDocumentsWithAI);
  const [{ fetching: exporting }, exportData] = useGlobalAction(api.generateDocumentReport);
  const [{ fetching: sendingNotification }, sendNotification] = useGlobalAction(api.sendEvaluationNotification);

  // Model actions for overrides
  const [{ fetching: updatingShift }, updateShiftProcessing] = useAction(api.shiftProcessing.update);

  // Handle filter changes
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle document selection
  const handleDocSelection = useCallback((docId, checked) => {
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(docId);
      } else {
        newSet.delete(docId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
    if (checked && documents) {
      setSelectedDocs(new Set(documents.map(doc => doc.id)));
    } else {
      setSelectedDocs(new Set());
    }
  }, [documents]);

  // Handle individual reprocess
  const handleReprocess = useCallback(async (docId) => {
    try {
      await reprocessDocument({ documentId: docId });
      toast({
        title: "Document reprocessing started",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      refetch();
    } catch (error) {
      toast({
        title: "Failed to reprocess document",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [reprocessDocument, toast, refetch]);

  // Handle batch reprocess
  const handleBatchReprocess = useCallback(async () => {
    if (selectedDocs.size === 0) return;
    
    try {
      await batchProcess({ 
        documentIds: Array.from(selectedDocs) 
      });
      toast({
        title: `${selectedDocs.size} documents queued for reprocessing`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setSelectedDocs(new Set());
      setSelectAll(false);
      onReprocessModalClose();
      refetch();
    } catch (error) {
      toast({
        title: "Failed to reprocess documents",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedDocs, batchProcess, toast, onReprocessModalClose, refetch]);

  // Handle export
  const handleExport = useCallback(async () => {
    try {
      const result = await exportData({
        filter: apiFilter,
        format: "csv"
      });
      toast({
        title: "Export completed",
        description: "Document data has been exported successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onExportModalClose();
    } catch (error) {
      toast({
        title: "Export failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [apiFilter, exportData, toast, onExportModalClose]);

  // Handle override to pass and release
  const handleOverridePassRelease = useCallback(async () => {
    if (!selectedDocForOverride) return;
    
    try {
      await updateShiftProcessing({
        id: selectedDocForOverride.id,
        auditVerdict: "pass"
      });
      
      await sendNotification({
        shiftProcessingId: selectedDocForOverride.id,
        notificationType: "pass"
      });
      
      toast({
        title: "Override applied successfully",
        description: "Shift has been overridden to pass and student notification sent",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      onOverridePassReleaseClose();
      setSelectedDocForOverride(null);
      refetch();
    } catch (error) {
      toast({
        title: "Override failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedDocForOverride, updateShiftProcessing, sendNotification, toast, onOverridePassReleaseClose, refetch]);

  // Handle override to pass without release
  const handleOverridePassNoRelease = useCallback(async () => {
    if (!selectedDocForOverride) return;
    
    try {
      await updateShiftProcessing({
        id: selectedDocForOverride.id,
        auditVerdict: "pass"
      });
      
      toast({
        title: "Override applied successfully",
        description: "Shift has been overridden to pass (no notification sent)",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      onOverridePassNoReleaseClose();
      setSelectedDocForOverride(null);
      refetch();
    } catch (error) {
      toast({
        title: "Override failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedDocForOverride, updateShiftProcessing, toast, onOverridePassNoReleaseClose, refetch]);

  // Handle release as failed
  const handleReleaseFailed = useCallback(async () => {
    if (!selectedDocForOverride) return;
    
    try {
      await sendNotification({
        shiftProcessingId: selectedDocForOverride.id,
        notificationType: "fail"
      });
      
      toast({
        title: "Notification sent",
        description: "Student has been notified of the failed evaluation",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      
      onReleaseFailedClose();
      setSelectedDocForOverride(null);
      refetch();
    } catch (error) {
      toast({
        title: "Notification failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedDocForOverride, sendNotification, toast, onReleaseFailedClose, refetch]);

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'analysis_complete':
        return 'green';
      case 'uploaded':
      case 'extracting_text':
      case 'analyzing':
        return 'blue';
      case 'failed':
        return 'red';
      case 'requires_review':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'normal':
        return 'blue';
      case 'low':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!documents) return {};
    
    const total = documents.length;
    const completed = documents.filter(d => 
      d.processingStatus === 'analysis_complete' || d.processingStatus === 'completed'
    ).length;
    const failed = documents.filter(d => d.processingStatus === 'failed').length;
    const processing = documents.filter(d => 
      ['uploaded', 'extracting_text', 'analyzing'].includes(d.processingStatus)
    ).length;
    const avgPatientCount = documents
      .filter(d => d.structuredData?.patients)
      .reduce((sum, d) => sum + (d.structuredData?.patients?.length || 0), 0) / 
      documents.filter(d => d.structuredData?.patients).length || 0;
    
    return { total, completed, failed, processing, avgPatientCount };
  }, [documents]);

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Failed to load documents: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Flex align="center" justify="space-between" mb={4}>
            <Box>
              <Heading size="lg" mb={2}>Clinical Document Management</Heading>
              <Text color="gray.600">
                Manage and process clinical documents including shift reports and patient encounters
              </Text>
            </Box>
            <Button
              as={Link}
              to="/test-clinical-docs"
              colorScheme="teal"
              size="lg"
              leftIcon={<FiFileText />}
              variant="outline"
            >
              Test Clinical Document Reader
            </Button>
          </Flex>
        </Box>

        {/* Summary Stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Documents</StatLabel>
                <StatNumber>{summaryStats.total || 0}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Completed</StatLabel>
                <StatNumber>{summaryStats.completed || 0}</StatNumber>
                <StatHelpText color="green.500">Processing complete</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Processing</StatLabel>
                <StatNumber>{summaryStats.processing || 0}</StatNumber>
                <StatHelpText color="blue.500">In progress</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Avg Patients</StatLabel>
                <StatNumber>{summaryStats.avgPatientCount?.toFixed(1) || '0.0'}</StatNumber>
                <StatHelpText>Patients per shift</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Filters */}
        <Card>
          <CardBody>
            <VStack spacing={4}>
              <Flex align="center" w="full">
                <HStack spacing={2}>
                  <FiFilter />
                  <Text fontWeight="semibold">Filters</Text>
                </HStack>
                <Spacer />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setFilters({
                    institution: "",
                    processingStatus: "",
                    dateFrom: "",
                    dateTo: "",
                    search: ""
                  })}
                >
                  Clear All
                </Button>
              </Flex>
              
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                <FormControl>
                  <FormLabel>Institution</FormLabel>
                  <Input
                    placeholder="Filter by institution"
                    value={filters.institution}
                    onChange={(e) => handleFilterChange('institution', e.target.value)}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Processing Status</FormLabel>
                  <Select
                    placeholder="All statuses"
                    value={filters.processingStatus}
                    onChange={(e) => handleFilterChange('processingStatus', e.target.value)}
                  >
                    <option value="uploaded">Uploaded</option>
                    <option value="extracting_text">Extracting Text</option>
                    <option value="text_extracted">Text Extracted</option>
                    <option value="analyzing">Analyzing</option>
                    <option value="analysis_complete">Analysis Complete</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="requires_review">Requires Review</option>
                  </Select>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Search</FormLabel>
                  <Input
                    placeholder="Search documents..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </FormControl>
              </SimpleGrid>
              
              <HStack spacing={4} w="full">
                <FormControl maxW="200px">
                  <FormLabel>From Date</FormLabel>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </FormControl>
                
                <FormControl maxW="200px">
                  <FormLabel>To Date</FormLabel>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </FormControl>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Batch Actions */}
        {selectedDocs.size > 0 && (
          <Card bg="blue.50" borderColor="blue.200">
            <CardBody>
              <Flex align="center">
                <Text fontWeight="semibold">
                  {selectedDocs.size} document{selectedDocs.size > 1 ? 's' : ''} selected
                </Text>
                <Spacer />
                <ButtonGroup>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<FiPlay />}
                    onClick={onReprocessModalOpen}
                    isLoading={batchProcessing}
                  >
                    Reprocess Selected
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<FiDownload />}
                    onClick={onExportModalOpen}
                  >
                    Export Selected
                  </Button>
                </ButtonGroup>
              </Flex>
            </CardBody>
          </Card>
        )}

        {/* Documents Table */}
        <Card>
          <CardBody>
            <Flex align="center" mb={4}>
              <Text fontSize="lg" fontWeight="semibold">Documents</Text>
              <Spacer />
              <ButtonGroup>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiRefreshCw />}
                  onClick={refetch}
                  isLoading={fetching}
                >
                  Refresh
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<FiDownload />}
                  onClick={onExportModalOpen}
                  isLoading={exporting}
                >
                  Export All
                </Button>
              </ButtonGroup>
            </Flex>

            {fetching && !documents ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" />
              </Flex>
            ) : documents && documents.length > 0 ? (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>
                        <Checkbox
                          isChecked={selectAll}
                          isIndeterminate={selectedDocs.size > 0 && selectedDocs.size < documents.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </Th>
                      <Th>Shift Processing</Th>
                      <Th>Institution</Th>
                      <Th>Status</Th>
                      <Th>Evaluation</Th>
                      <Th>Audit Verdict</Th>
                      <Th>Validation</Th>
                      <Th>Patients</Th>
                      <Th>Created</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {documents.map((doc) => (
                      <Tr key={doc.id}>
                        <Td>
                          <Checkbox
                            isChecked={selectedDocs.has(doc.id)}
                            onChange={(e) => handleDocSelection(doc.id, e.target.checked)}
                          />
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="semibold" fontSize="sm">
                              {doc.userFile?.title || doc.userFile?.originalFilename || 'Untitled Document'}
                            </Text>
                            <HStack spacing={2}>
                              <Badge size="sm" colorScheme="purple">
                                Shift {doc.sourceShiftId}
                              </Badge>
                              {doc.processor && (
                                <Badge size="sm" colorScheme="blue">
                                  {doc.processor.name}
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {doc.submittedBy?.institution || 'Unknown'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {doc.submittedBy?.firstName} {doc.submittedBy?.lastName}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(doc.processingStatus)}>
                            {doc.processingStatus}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(doc.evaluationStatus)}>
                            {doc.evaluationStatus}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge 
                            colorScheme={
                              doc.auditVerdict === 'pass' ? 'green' : 
                              doc.auditVerdict === 'fail' ? 'red' : 'gray'
                            }
                          >
                            {doc.auditVerdict || 'pending'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(doc.validationStatus)}>
                            {doc.validationStatus}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {doc.structuredData?.patients?.length || 0}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {new Date(doc.createdAt).toLocaleTimeString()}
                          </Text>
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<FiMoreVertical />}
                              variant="ghost"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<FiEye />}
                                as={Link}
                                to={`/admin/shift-processing/${doc.id}`}
                              >
                                View Details
                              </MenuItem>
                              <MenuItem
                                icon={<FiRefreshCw />}
                                onClick={() => handleReprocess(doc.id)}
                                isDisabled={reprocessing}
                              >
                                Reprocess
                              </MenuItem>
                              {doc.processingStatus === 'failed' && (
                                <MenuItem
                                  icon={<FiAlertCircle />}
                                  color="red.500"
                                >
                                  View Error Log
                                </MenuItem>
                              )}
                              {doc.auditVerdict === 'fail' && (
                                <>
                                  <MenuDivider />
                                  <MenuItem
                                    color="orange.500"
                                    fontWeight="bold"
                                    onClick={() => {
                                      setSelectedDocForOverride(doc);
                                      onOverridePassReleaseOpen();
                                    }}
                                    isDisabled={updatingShift || sendingNotification}
                                  >
                                    ⚠️ Override to Pass & Release
                                  </MenuItem>
                                  <MenuItem
                                    color="orange.500"
                                    fontWeight="bold"
                                    onClick={() => {
                                      setSelectedDocForOverride(doc);
                                      onOverridePassNoReleaseOpen();
                                    }}
                                    isDisabled={updatingShift}
                                  >
                                    ⚠️ Override to Pass (No Release)
                                  </MenuItem>
                                  <MenuItem
                                    color="red.500"
                                    fontWeight="bold"
                                    onClick={() => {
                                      setSelectedDocForOverride(doc);
                                      onReleaseFailedOpen();
                                    }}
                                    isDisabled={sendingNotification}
                                  >
                                    📤 Release as Failed
                                  </MenuItem>
                                </>
                              )}
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <VStack spacing={4} py={8}>
                <Text color="gray.500">No documents found</Text>
                <Button
                  colorScheme="blue"
                  onClick={() => navigate('/admin/document-processing/upload')}
                >
                  Upload Documents
                </Button>
              </VStack>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Reprocess Confirmation Modal */}
      <Modal isOpen={isReprocessModalOpen} onClose={onReprocessModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Reprocessing</ModalHeader>
          <ModalBody>
            <Text>
              Are you sure you want to reprocess {selectedDocs.size} document
              {selectedDocs.size > 1 ? 's' : ''}? This will override any existing analysis results.
            </Text>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="outline" onClick={onReprocessModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleBatchReprocess}
                isLoading={batchProcessing}
              >
                Reprocess
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Export Confirmation Modal */}
      <Modal isOpen={isExportModalOpen} onClose={onExportModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Export Documents</ModalHeader>
          <ModalBody>
            <Text>
              Export {selectedDocs.size > 0 
                ? `${selectedDocs.size} selected documents`
                : `all ${summaryStats.total} documents`
              } to CSV format?
            </Text>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="outline" onClick={onExportModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleExport}
                isLoading={exporting}
              >
                Export
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Override to Pass & Release Modal */}
      <Modal isOpen={isOverridePassReleaseOpen} onClose={onOverridePassReleaseClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="orange.500">⚠️ Administrative Override</ModalHeader>
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              This is an administrative override action
            </Alert>
            <Text>
              Are you sure you want to override the failed evaluation for this shift 
              to <strong>PASS</strong> and send a release notification to the student?
            </Text>
            <Text mt={2} fontSize="sm" color="gray.600">
              Shift ID: {selectedDocForOverride?.sourceShiftId}
            </Text>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="outline" onClick={onOverridePassReleaseClose}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleOverridePassRelease}
                isLoading={updatingShift || sendingNotification}
              >
                Override to Pass & Release
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Override to Pass (No Release) Modal */}
      <Modal isOpen={isOverridePassNoReleaseOpen} onClose={onOverridePassNoReleaseClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="orange.500">⚠️ Administrative Override</ModalHeader>
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              This is an administrative override action
            </Alert>
            <Text>
              Are you sure you want to override the failed evaluation for this shift 
              to <strong>PASS</strong> without sending a notification to the student?
            </Text>
            <Text mt={2} fontSize="sm" color="gray.600">
              Shift ID: {selectedDocForOverride?.sourceShiftId}
            </Text>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="outline" onClick={onOverridePassNoReleaseClose}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleOverridePassNoRelease}
                isLoading={updatingShift}
              >
                Override to Pass (No Release)
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Release as Failed Modal */}
      <Modal isOpen={isReleaseFailedOpen} onClose={onReleaseFailedClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="red.500">📤 Release Failed Evaluation</ModalHeader>
          <ModalBody>
            <Alert status="error" mb={4}>
              <AlertIcon />
              This will notify the student of the failed evaluation
            </Alert>
            <Text>
              Are you sure you want to release this <strong>FAILED</strong> evaluation 
              and send a notification to the student?
            </Text>
            <Text mt={2} fontSize="sm" color="gray.600">
              Shift ID: {selectedDocForOverride?.sourceShiftId}
            </Text>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="outline" onClick={onReleaseFailedClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleReleaseFailed}
                isLoading={sendingNotification}
              >
                Release as Failed
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default ClinicalDocsAdmin;